<template lang="pug">
div
  .workflow-fields__field
    .form-field-label
      span {{ workflowConstructorLocales.transition_form_fields }}
    .form-field.form-multiselect
      Multiselect(
        :value="transitionValidationFieldsValue",
        :track-by="'id'",
        :label="'title'",
        :options="transitionValidationFieldsOptions",
        placeholder="",
        :multiple="true",
        :hideSelected="true",
        :show-labels="false",
        open-direction="bottom",
        :allow-empty="true",
        :close-on-select="false",
        @select="selectBasicField",
        @remove="removeBasicField"
      )
        span(slot="noResult")
          i {{ notifies.no_search_result }}
        span(slot="noOptions")
          i {{ notifies.no_options_list }}

  .workflow-fields__field
    .form-field-label
      span {{ workflowConstructorLocales.select_required_form_fields }}
    .form-field.form-multiselect
      Multiselect(
        :value="transitionValidationFieldsRequiredValue",
        :track-by="'id'",
        :label="'title'",
        :options="transitionValidationFieldsValue",
        placeholder="",
        :multiple="true",
        :hideSelected="true",
        :show-labels="false",
        open-direction="bottom",
        :allow-empty="true",
        :close-on-select="false",
        @select="selectRequiredField",
        @remove="removeRequiredField"
      )
        span(slot="noResult")
          i {{ notifies.no_search_result }}
        span(slot="noOptions")
          i {{ notifies.no_options_list }}
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { workflowConstructorLocales } from "@/services/useLocales";

import Multiselect from "vue-multiselect";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";

const props = defineProps({
  transition: { type: Object, default: () => {} },
  workflowId: { type: Number, default: null },
});

const emit = defineEmits(["transitionValidationChanged"]);

const requestFields = ref([]);

const transitionValidationFieldsOptions = ref([]);
const transitionValidationFieldsValue = ref([]);

const transitionValidationFieldsRequiredValue = ref([]);

const getTransitionValidationFieldsValue = computed(() => {
  return props.transition.form.validations.map(elem => {
    return {
      id: elem.attribute,
      title: elem.attribute_title,
      validation_id: elem.id,
    };
  });
});

const getTransitionValidationFieldsOptions = async () => {
  try {
    transitionValidationFieldsOptions.value = (
      await backend.collection("api/v3/fields/collection", { workflow_id: props.workflowId })
    ).data.options;
  } catch (e) {
    await handleError(e);
  }
};

const getTransitionValidationFieldsRequiredValue = computed(() => {
  const transitionsWithRequired = props.transition.form.validations.filter(elem => {
    return elem.type === "required";
  });

  return transitionsWithRequired.map(elem => ({
    id: elem.attribute,
    title: elem.attribute_title,
    validation_id: elem.id,
  }));
});

const emitTransitionFieldsUpdated = () => {
  const req = {
    validationAttributes: requestFields.value,
  };

  emit("transitionValidationChanged", req);
};

const selectBasicField = event => {
  transitionValidationFieldsValue.value.push(event);

  const requestIndex = requestFields.value.findIndex(el => el.attribute === event.id);

  if (requestIndex === -1 && !event.validation_id)
    requestFields.value.push({
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      attribute: event.id,
      type: null,
    });

  emitTransitionFieldsUpdated();
};

const selectRequiredField = event => {
  transitionValidationFieldsRequiredValue.value.push(event);

  const requestIndex = requestFields.value.findIndex(el => el.attribute === event.id);

  if (requestIndex !== -1) {
    requestFields.value[requestIndex] = {
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      id: event.validation_id ? event.validation_id : null,
      attribute: event.id,
      type: "required",
    };
  } else {
    requestFields.value.push({
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      id: event.validation_id ? event.validation_id : null,
      attribute: event.id,
      type: "required",
    });
  }

  emitTransitionFieldsUpdated();
};

const removeBasicField = event => {
  const index = transitionValidationFieldsValue.value.findIndex(el => el.id === event.id);

  if (index !== -1) {
    transitionValidationFieldsValue.value.splice(index, 1);
  }

  const reqIndex = transitionValidationFieldsRequiredValue.value.findIndex(el => el.id === event.id);

  if (reqIndex !== -1) {
    transitionValidationFieldsRequiredValue.value.splice(reqIndex, 1);
  }

  const requestIndex = requestFields.value.findIndex(el => el.attribute === event.id);

  if (requestIndex !== -1 && !event.validation_id) {
    requestFields.value.splice(requestIndex, 1);
  } else {
    requestFields.value.push({
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      id: event.validation_id ? event.validation_id : null,
      attribute: event.id,
      _destroy: true,
    });
  }

  emitTransitionFieldsUpdated();
};

const removeRequiredField = event => {
  const index = transitionValidationFieldsRequiredValue.value.findIndex(el => el.id === event.id);

  if (index !== -1) {
    transitionValidationFieldsRequiredValue.value.splice(index, 1);
  }

  const requestIndex = requestFields.value.findIndex(el => el.attribute === event.id);

  if (requestIndex !== -1) {
    requestFields.value[requestIndex] = {
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      id: event.validation_id ? event.validation_id : null,
      attribute: event.id,
      type: null,
    };
  } else {
    requestFields.value.push({
      entity_type: "TransitionForm",
      entity_id: props.transition.id,
      id: event.validation_id ? event.validation_id : null,
      attribute: event.id,
      type: null,
    });
  }

  emitTransitionFieldsUpdated();
};

// const findValidationEquation = (currentElement, searchingElement) => {
//   // if field_id presented in object we should find using field_id and searchingElement id,
//   // otherwise - using id and searchingElement relation_id
//   return currentElement.field_id
//     ? currentElement.field_id === searchingElement.id
//     : currentElement.id === searchingElement.validation_id;
// };

// const filterValidationsEquation = (currentElement, filterElementsParam) => {
//   // if field_id presented in object we should filter using field_id and filterElementsParam id,
//   // otherwise - using id and filterElementsParam relation_id
//   return currentElement.field_id
//     ? currentElement.field_id !== filterElementsParam.id
//     : currentElement.id !== filterElementsParam.relation_id;
// };

onMounted(async () => {
  await getTransitionValidationFieldsOptions();
  transitionValidationFieldsValue.value = getTransitionValidationFieldsValue.value;
  transitionValidationFieldsRequiredValue.value = getTransitionValidationFieldsRequiredValue.value;
});
</script>
